import type { MyRestaurant } from "@luckymenu/core";
import { useTranslation } from "@luckymenu/i18n/react";
import { useImageSources, useMe, usePlatform, useSwitchRestaurant } from "@luckymenu/staff-core";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@luckymenu/ui-web";
import { ChevronRight, LogOut, Store } from "lucide-react";
import { Centered } from "./centered";
import { JoinCodeForm } from "./join-code-form";
import { OwnerBilling } from "./owner-billing";

function SignedInAs() {
	const { t } = useTranslation(["common", "nav"]);
	const me = useMe();
	const platform = usePlatform();
	return (
		<div className="flex flex-col items-center gap-2 text-center">
			{me.data?.email ? (
				<p className="text-sm text-muted-foreground">{t("signedInAs", { name: me.data.email })}</p>
			) : null}
			<Button variant="ghost" onClick={() => void platform.auth.signOut()}>
				<LogOut data-icon="inline-start" />
				{t("nav:signOut")}
			</Button>
		</div>
	);
}

/** Signed in, but a member of no restaurant yet. */
export function NoRestaurantScreen() {
	const { t } = useTranslation(["auth", "staff"]);
	return (
		<Centered>
			<Card>
				<CardHeader>
					<CardTitle>
						<h1 className="text-xl">{t("noRestaurantTitle")}</h1>
					</CardTitle>
					<CardDescription>{t("noRestaurantBody")}</CardDescription>
				</CardHeader>
				<CardContent>
					<JoinCodeForm />
				</CardContent>
			</Card>
			<SignedInAs />
		</Centered>
	);
}

function RestaurantLogo({ restaurant }: { restaurant: MyRestaurant }) {
	const sources = useImageSources(restaurant.logoFileId ? { id: restaurant.logoFileId } : null, 40);
	return (
		<span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-button bg-muted">
			{sources ? (
				<img
					src={sources.src}
					srcSet={sources.srcSet}
					sizes="40px"
					alt=""
					className="size-full object-cover"
				/>
			) : (
				<Store className="size-5 text-muted-foreground" aria-hidden />
			)}
		</span>
	);
}

/** Several memberships and no choice yet. */
export function ChooserScreen({ restaurants }: { restaurants: MyRestaurant[] }) {
	const { t } = useTranslation(["staff", "common"]);
	const switchRestaurant = useSwitchRestaurant();
	return (
		<Centered>
			<Card>
				<CardHeader>
					<CardTitle>
						<h1 className="text-xl">{t("chooser.title")}</h1>
					</CardTitle>
					<CardDescription>{t("chooser.body")}</CardDescription>
				</CardHeader>
				<CardContent>
					<ul className="grid gap-2">
						{restaurants.map((r) => (
							<li key={r.id}>
								<button
									type="button"
									onClick={() => void switchRestaurant(r.id)}
									className="flex min-h-14 w-full items-center gap-3 rounded-card border px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
								>
									<RestaurantLogo restaurant={r} />
									<span className="min-w-0 flex-1">
										<span className="block truncate font-medium">{r.name}</span>
										<span className="block text-xs text-muted-foreground">
											{t(`common:roles.${r.role}`)}
										</span>
									</span>
									<ChevronRight className="size-4 text-muted-foreground" aria-hidden />
								</button>
							</li>
						))}
					</ul>
				</CardContent>
			</Card>
			<SignedInAs />
		</Centered>
	);
}

/** The session could not load. */
export function SessionErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<Centered>
			<p role="alert" className="text-center text-sm text-muted-foreground">
				{message}
			</p>
			<Button onClick={onRetry}>{t("retry")}</Button>
		</Centered>
	);
}

/** The restaurant is suspended: operations stop until LuckyMenu restores it. */
export function SuspendedScreen({
	restaurant,
	canSwitch,
	ownerBilling = false,
}: {
	restaurant: string;
	canSwitch: boolean;
	/** The owner (`subscription.view`) sees what is due and the invoices. */
	ownerBilling?: boolean;
}) {
	const { t } = useTranslation("staff");
	const switchRestaurant = useSwitchRestaurant();
	return (
		<Centered>
			<Card>
				<CardHeader>
					<CardTitle>
						<h1 className="text-xl">{t("suspended.title", { restaurant })}</h1>
					</CardTitle>
					<CardDescription>{t("suspended.body")}</CardDescription>
				</CardHeader>
				{ownerBilling ? (
					<CardContent>
						<OwnerBilling />
					</CardContent>
				) : null}
				{canSwitch ? (
					<CardContent>
						<Button
							variant="outline"
							className="w-full"
							onClick={() => void switchRestaurant(null)}
						>
							{t("suspended.switch")}
						</Button>
					</CardContent>
				) : null}
			</Card>
			<SignedInAs />
		</Centered>
	);
}
